import React, { useState } from 'react'
import { ReactComponent as OverviewIcon } from 'libs/shared/assets/src/lib/icons/overviewIcon.svg'
import { ReactComponent as InfoIcon } from 'libs/shared/assets/src/lib/icons/infoSimple.svg'
import { useTranslation } from 'react-i18next'
import { NoPrint, Tooltip, AvoidPrintPageBreak, Badge } from '@dataplace.ai/ui-components/atoms'
import useCheckIfPrinting from 'apps/placeme/src/customHooks/useCheckIfPrinting'
import { useCheckForDesktopMediumDevice, useCheckForMobileDevice, theme } from '@dataplace.ai/ui-components/themes'
import { AverageTraffic, AverageTrafficData } from '../AverageTraffic'
import { TrafficMap, TrafficMapData } from '../TrafficMap'
import { OverviewWeekTraffic, OverviewWeekTrafficData } from '../weekTraffic'
import { TrafficByDaypart, TrafficByDaypartData } from '../TrafficByDaypart'
import { People, PeopleData } from '../People'
import { OverviewDemography, OverviewDemographyData } from '../demography'
import { OverviewPurchasingPower, OverviewPurchasingPowerData } from '../purchasingPower'
import { overviewDummyData } from '../dummyData'
import { useOverviewData } from '../hooks/useOverviewData'
import {
  Chevron,
  HeaderBox,
  LLMSummaryBadge,
  LLMSummaryBadgeIcon,
  LLMSummaryHeader,
  LLMSummaryWrapper,
  OverviewColumn,
  OverviewContent,
  OverviewWrapper,
  TitleBox,
  Wrapper,
} from './OverviewTile.styles'
import { Overlay } from './Overlay'

import { ConditionalNoPrint } from './ConditionalNoPrint'
import { NO_OVERVIEW_DATA } from '../constants'
import { MarkdownRenderer } from './MarkdownRenderer'

export interface OverviewData {
  traffic: {
    averageTraffic: AverageTrafficData
    map: TrafficMapData
    weekTraffic: OverviewWeekTrafficData
    dailyTraffic: TrafficByDaypartData
  }
  population: {
    series: PeopleData
    demography: OverviewDemographyData
  }
  income: OverviewPurchasingPowerData
  llmSummary?: string | typeof NO_OVERVIEW_DATA
}

export function OverviewTile(): JSX.Element {
  const { resultData } = useOverviewData()
  const data = resultData ?? overviewDummyData
  const { t } = useTranslation()
  const [isOpened, setIsOpened] = useState(true)
  const { isPrinting } = useCheckIfPrinting()
  const mediumDesktop = useCheckForDesktopMediumDevice()
  const mobile = useCheckForMobileDevice()
  const {
    palette, typography,
  } = theme()
  const overviewContentWidth = isPrinting
    ? 'fit-content'
    : mobile || mediumDesktop
      ? 'calc(486px + 455px + 1rem)'
      : 'calc(409px + 256px + 397px + 2rem)'

  const toggleIsOpened = () => setIsOpened(prev => !prev)

  return (
    <ConditionalNoPrint>
      <AvoidPrintPageBreak>
        <Wrapper
          $isPrinting={isPrinting}
          className='pdf-tile'
        >
          <HeaderBox>
            <TitleBox>
              <OverviewIcon />
              {t('placeme.overview.title')}
              <NoPrint>
                <Tooltip
                  content={t('placeme.overview.info_tooltip.text')}
                  display='flex'
                  maxWidth='25rem'
                  position='bottom center'
                >
                  <InfoIcon
                    height={14}
                    width={14}
                  />
                </Tooltip>
              </NoPrint>
            </TitleBox>
            <NoPrint>
              <Chevron
                $isOpened={isOpened}
                onClick={toggleIsOpened}
              />
            </NoPrint>
          </HeaderBox>
          {!isOpened
            ? null
            : (
              <Overlay>
                <OverviewWrapper
                  $contentWidth={overviewContentWidth}
                  $isPrinting={isPrinting}
                >
                  {data?.llmSummary && data.llmSummary !== NO_OVERVIEW_DATA
                    ? (
                      <LLMSummaryWrapper>
                        <LLMSummaryHeader>
                          <LLMSummaryBadge $isPrinting={isPrinting}>
                            <LLMSummaryBadgeIcon
                              aria-hidden='true'
                              height={isPrinting ? 10 : 14}
                              width={isPrinting ? 10 : 14}
                            />
                            <span>{t('placeme.overview.ai_badge.label')}</span>
                            <NoPrint>
                              <Tooltip
                                content={t('placeme.overview.ai_badge.tooltip')}
                                display='flex'
                                maxWidth='25rem'
                                position='bottom center'
                              >
                                <InfoIcon
                                  height={isPrinting ? 10 : 14}
                                  width={isPrinting ? 10 : 14}
                                />
                              </Tooltip>
                            </NoPrint>
                          </LLMSummaryBadge>
                          <Badge
                            backgroundColor={palette.results.green.dark}
                            fontSize={typography.tiny.pt_12_semibold_upper.fontSize}
                            label={t('placeme.generic.beta')}
                          />
                        </LLMSummaryHeader>
                        <MarkdownRenderer markdown={data.llmSummary} />
                      </LLMSummaryWrapper>
                    )
                    : null}
                  <OverviewContent $isPrinting={isPrinting}>
                    {mobile || mediumDesktop
                      ? (
                        <>
                          <OverviewColumn
                            $isPrinting={isPrinting}
                            $width='486px'
                          >
                            {data?.traffic.averageTraffic && <AverageTraffic data={data.traffic.averageTraffic} />}
                            {data?.traffic.map && <TrafficMap data={data.traffic.map} />}
                            {data?.population.demography && <OverviewDemography data={data.population.demography} />}
                          </OverviewColumn>
                          <OverviewColumn
                            $isPrinting={isPrinting}
                            $width='455px'
                          >
                            {data?.traffic.weekTraffic && <OverviewWeekTraffic data={data.traffic.weekTraffic} />}
                            {data?.traffic.dailyTraffic && <TrafficByDaypart data={data.traffic.dailyTraffic} />}
                            {data?.population.series && <People data={data.population.series} />}
                            {data?.income && <OverviewPurchasingPower {...data.income} />}
                          </OverviewColumn>
                        </>
                      )
                      : (
                        <>
                          <OverviewColumn
                            $isPrinting={isPrinting}
                            $width='409px'
                          >
                            {data?.traffic.averageTraffic && <AverageTraffic data={data.traffic.averageTraffic} />}
                            {data?.traffic.map && <TrafficMap data={data.traffic.map} />}
                          </OverviewColumn>
                          <OverviewColumn
                            $isPrinting={isPrinting}
                            $width='256px'
                          >
                            {data?.traffic.weekTraffic && <OverviewWeekTraffic data={data.traffic.weekTraffic} />}
                            {data?.traffic.dailyTraffic && <TrafficByDaypart data={data.traffic.dailyTraffic} />}
                            {data?.income && <OverviewPurchasingPower {...data.income} />}
                          </OverviewColumn>
                          <OverviewColumn
                            $isPrinting={isPrinting}
                            $width='397px'
                          >
                            {data?.population.series && <People data={data.population.series} />}
                            {data?.population.demography && <OverviewDemography data={data.population.demography} />}
                          </OverviewColumn>
                        </>
                      )}

                  </OverviewContent>
                </OverviewWrapper>
              </Overlay>
            )}
        </Wrapper>
      </AvoidPrintPageBreak>
    </ConditionalNoPrint>
  )
}
